import React from "react";
import { Box, Chip, Stack, Typography } from "@mui/material";
import { ActionButton, Backdrop, CustomTooltip, SelectField } from ".";

/**
 * A component that displays a page header with a title, subtitle, and optional
 * action buttons, select fields, chip tabs, and a custom element slot.
 *
 * @param {Object}   props              - Component props object.
 * @param {string}   [props.title=""]         - Main heading displayed at the top-left of the header.
 * @param {string}   [props.subtitle=""]      - Secondary text shown below the title.
 * @param {Array<{buttonText: string, onClick: Function, buttonIcon?: ReactNode, disabled?: boolean, style?: object}>} [props.buttons=[]]
 *   - Action buttons rendered in the top-right area.
 * @param {Array<{label: string, value: any, fetchData: Function, filters?: object, onChange: Function}>} [props.inputFields=[]]
 *   - Select/dropdown fields rendered alongside the buttons.
 * @param {Array<{label: string, onClick: Function, sx?: object}>} [props.chipItems=[]]
 *   - Chip items used as tab selectors below the title.
 * @param {string}   [props.activeTab=""]     - The currently active chip tab (matched against `chip.label.toLowerCase()`).
 * @param {ReactNode} [props.customElement=null] - An arbitrary React node rendered at the end of the right-side slot,
 *   useful for inserting custom controls that don't fit the buttons/inputFields patterns.
 * @returns {ReactElement} The page header component.
 */
export const PageHeader = ({
  title = "",
  subtitle = "",
  buttons = [],
  inputFields = [],
  chipItems = [],
  activeTab = "",
  customElement = null,
}) => {
  return (
    <Box
      sx={{
        display: "flex",
        justifyContent: "space-between",
        alignItems: "top",
        position: "relative",
        backgroundColor: "#674BF5",
        height: 150,
        padding: "28px 22px",
        overflow: "hidden",
      }}
    >
      <Backdrop position={{ left: "20%", bottom: "65%" }} />
      <Backdrop position={{ left: "40%", top: "20%" }} />
      <Backdrop position={{ right: "20%", bottom: "65%" }} />

      <Box sx={{ zIndex: 2, maxWidth: "60%" }}>
        <CustomTooltip title={title}>
          <Typography
            variant="h6"
            style={{
              color: "#fff",
              textTransform: "capitalize",
              fontWeight: 500,
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
              cursor: "default",
            }}
          >
            {title}
          </Typography>
        </CustomTooltip>

        <Typography variant="caption" style={{ color: "rgba(255, 255, 255, 0.7)" }}>
          {subtitle}
        </Typography>

        {chipItems.length > 0 && (
          <Box sx={{ marginTop: 2 }}>
            <Stack direction="row" spacing={1}>
              {chipItems.map((chip, index) => (
                <Chip
                  key={index}
                  label={chip.label}
                  onClick={chip.onClick}
                  sx={{
                    backgroundColor: activeTab === chip.label.toLowerCase() ? "#FFF" : "#422BB5",
                    color: activeTab === chip.label.toLowerCase() ? "#674BF5" : "#FFF",
                    borderRadius: "16px",
                    fontSize: "0.75rem",
                    fontWeight: 500,
                    textTransform: "capitalize",

                    "&:hover": {
                      backgroundColor: chip.label.toLowerCase() ? "#f5f5f5" : "#563cc6",
                      color: chip.label.toLowerCase() ? "#674BF5" : "#FFF",
                    },
                    ...chip.sx,
                  }}
                />
              ))}
            </Stack>
          </Box>
        )}
      </Box>

      <Box
        sx={{
          display: "flex",
          gap: 2,
        }}
      >
        {inputFields.map((field, index) => (
          <SelectField
            label={field.label}
            value={field.value}
            getData={field.fetchData}
            getDataStaticFilters={field.filters}
            onChange={field.onChange}
            disableClearable={field.disableClearable}
            style={{
              width: 200,
              "& .MuiOutlinedInput-root": {
                color: "#fff",
                "& fieldset": { borderColor: "#fff" },
                "&:hover fieldset": { borderColor: "#fff" },
                "&.Mui-focused fieldset": { borderColor: "#fff" },
              },
              "& .MuiInputBase-input": {
                color: "#fff",
              },
              "& .MuiInputLabel-root": {
                color: "#fff",
                "&.Mui-focused": { color: "#fff" },
              },
              "& .MuiSvgIcon-root": {
                color: "#fff",
              },
            }}
          />
        ))}
        {buttons.map((button, index) => {
          if (!button) return null; // Skip if button is undefined or null
          return (
            <ActionButton
              key={index}
              buttonText={button.buttonText}
              onButtonClick={button.onClick}
              buttonIcon={button.buttonIcon}
              disabled={button?.disabled}
              sx={{
                backgroundColor: "white",
                color: "#674BF5",
                textTransform: "capitalize",
                fontWeight: 500,
                height: 32,
                fontSize: "0.75rem",
                padding: "4px 10px",
                minWidth: "auto",
                ...button.style,
                "&.Mui-disabled": {
                  backgroundColor: "#FAF9F6",
                  color: "#7D5DF7",
                  opacity: 0.6,
                },
              }}
            />
          );
        })}
        {customElement}
      </Box>
    </Box>
  );
};
